// @flow
import * as React from 'react';
// eslint-disable-next-line import/named
import type { IntlShape } from 'react-intl';
import cn from 'classnames';

// Components
import {
  Container,
  Row,
  Col,
  DropdownToggle,
  DropdownMenu,
  DropdownItem
} from 'reactstrap';
import AppointmentFlowWizard from 'components/templates/AppointmentFlowWizard/';
import AppointmentFlowFooter from 'components/molecules/AppointmentFlowFooter/';
import SelfCheckInHeader from 'components/atoms/SelfCheckInHeader/';
import { TextFieldWithIcon as TextField } from 'components/api';
import Dropdown from 'components/atoms/Dropdown/';
import { Icon } from 'components/atoms/Icon';
import MobileDropdown from 'components/molecules/MobileDropdown';
import CtaStepIndicator from 'components/atoms/CtaStepIndicator';

// Constants
import {
  SCI_HOME_ROUTE,
  SCI_SERVICES_ROUTE,
  getCarColors
} from 'constants/selfCheckIn';
import { NAVIGATION } from 'constants/navigations';
import { labelsStepIndicatorSCI } from 'constants/CTAConfig';

import './MileagePage.scss';

const ns = 'mileage-page';
const { FORWARD } = NAVIGATION;

export type Props = {
  intl: IntlShape,
  goTo: (path: string, direction: string) => void,
  setMileage: (mileage: number) => void,
  gtmUserEventMileageEntered: void => void,
  gtmUserEventLicensePlateEntered: void => void,
  gtmUserEventColorEntered: void => void,
  setLicenseNumber: (licenseNumber: string) => void,
  setCarColor: (color: string) => void,
  getSelfCheckinMileage: (mileage: number) => void,
  mileage: number,
  selfCheckinMileage: number,
  carColor: string,
  licenseNumber: string,
  showSCIProgressBar: boolean
};

class MileagePage extends React.Component<Props> {
  constructor(props: Props) {
    super(props);
    this.onMileageChange = this.onMileageChange.bind(this);
    this.saveMileage = this.saveMileage.bind(this);
  }

  onMileageChange = (event: Object) => {
    const { setMileage } = this.props;
    const value = event.target.value;
    setMileage(value.replace(/[^0-9]/g, ''));
  };

  onLicenseChange = (ev: Object) => {
    const { setLicenseNumber } = this.props;
    setLicenseNumber(ev.target.value);
  };

  onDropdownToggle = (selectedColor: Object) => {
    const { setCarColor } = this.props;
    setCarColor(selectedColor.value);
  };

  onMobileDropdownToggle = (selectedColor: string) => {
    const { setCarColor, intl } = this.props;
    const colorArray = getCarColors(intl).filter(
      color => color.text === selectedColor
    )[0];
    setCarColor(colorArray.value);
  };

  saveMileage = () => {
    const {
      selfCheckinMileage,
      mileage,
      setMileage,
      gtmUserEventMileageEntered,
      gtmUserEventLicensePlateEntered,
      gtmUserEventColorEntered,
      licenseNumber,
      carColor
    } = this.props;
    setMileage(selfCheckinMileage || mileage);
    mileage && gtmUserEventMileageEntered();
    licenseNumber && gtmUserEventLicensePlateEntered();
    carColor && gtmUserEventColorEntered();
  };

  buildDropdown = () => {
    const { intl, carColor } = this.props;
    const selectedColor = this.getCarColorLabel(carColor);
    const dropdownLabel =
      selectedColor ||
      intl.formatMessage({
        id: 'c8.self.check.in.mileage.page.placeholder.color'
      });
    const dropdownLabelClassName = cn(`${ns}__dropdown-label`, {
      [`${ns}__dropdown-label--color-selected`]: carColor
    });
    return (
      <Dropdown
        className={`${ns}__dropdown`}
        onChange={e => this.onDropdownToggle(e)}
      >
        <DropdownToggle caret className={`d-flex ${ns}__dropdown-toggle`}>
          <Icon type="car_color" className={`${ns}__dropdown-icon`} />
          <div className={dropdownLabelClassName}>{dropdownLabel}</div>
        </DropdownToggle>
        {this.getDropdownMenu()}
      </Dropdown>
    );
  };

  getDropdownMenu = () => {
    const { intl } = this.props;
    const translatedColors = getCarColors(intl);
    const dropdownItems = translatedColors.map(color => (
      <DropdownItem
        onClick={() => this.onDropdownToggle(color)}
        key={color.text}
      >
        {color.text}
      </DropdownItem>
    ));
    return <DropdownMenu>{dropdownItems}</DropdownMenu>;
  };

  getCarColorLabel = (selectedColor: string) => {
    const { intl } = this.props;
    if (!selectedColor) {
      return '';
    } else {
      const colorArray = getCarColors(intl).filter(
        color => color.value === selectedColor
      )[0];
      return colorArray.text;
    }
  };

  render() {
    const {
      goTo,
      intl,
      selfCheckinMileage,
      licenseNumber,
      carColor,
      showSCIProgressBar
    } = this.props;
    const selectedColor = this.getCarColorLabel(carColor);
    const mileage = selfCheckinMileage > 0 ? selfCheckinMileage : '';
    const licenseNumberClassName = cn(`${ns}__form-fields`, {
      [`${ns}__form-fields--invalid`]: !licenseNumber
    });
    const carColorClassName = cn(`${ns}__form-fields`, {
      [`${ns}__form-fields--invalid`]: !carColor
    });
    const backButtonAriaLabel = intl.formatMessage({
      id: 'c8.aria.back.to.homepage',
      defaultMessage: 'Back to homepage'
    });
    return (
      <AppointmentFlowWizard
        isSelfCheckIn={true}
        className={ns}
        showSCIProgressBar={showSCIProgressBar}
        header={() =>
          showSCIProgressBar ? (
            <CtaStepIndicator
              htmlId="VehiclePageStepIndicator"
              labels={labelsStepIndicatorSCI(intl)}
              activeStep={1}
            />
          ) : (
            <SelfCheckInHeader
              label={''}
              route={SCI_HOME_ROUTE}
              ariaLabel={backButtonAriaLabel}
            />
          )
        }
        footer={() => (
          <AppointmentFlowFooter
            buttonDisabled={!licenseNumber || !carColor}
            buttonText={intl.formatMessage({
              id: 'c8.self.check.in.footer.next'
            })}
            onClick={() => {
              goTo(SCI_SERVICES_ROUTE, FORWARD);
              this.saveMileage();
            }}
          />
        )}
        body={() => (
          <React.Fragment>
            {showSCIProgressBar && (
              <SelfCheckInHeader
                label={intl.formatMessage({
                  id: 'c8.self.check.in.common.final.check.in'
                })}
                route={SCI_HOME_ROUTE}
                ariaLabel={backButtonAriaLabel}
              />
            )}
            <Container>
              <Row>
                <Col>
                  <h1 className={`${ns}__title mb-4`}>
                    {intl.formatMessage({
                      id: 'c8.self.check.in.mileage.page.vehicle.information'
                    })}
                  </h1>
                </Col>
              </Row>
              <Row>
                <Col>
                  <div className={`${ns}__fields-container`}>
                    <div className={`${ns}__form-fields`}>
                      <TextField
                        className={`${ns}__input-field`}
                        name="mileage_input"
                        placeholder={intl.formatMessage({
                          id:
                            'c8.self.check.in.mileage.page.placeholder.mileage'
                        })}
                        icon="mileage"
                        maxLength="7"
                        type="tel"
                        onChange={e => this.onMileageChange(e)}
                        value={mileage}
                      />
                    </div>
                    <div className={licenseNumberClassName}>
                      <TextField
                        className={`${ns}__input-field`}
                        name="license_input"
                        placeholder={intl.formatMessage({
                          id:
                            'c8.self.check.in.mileage.page.placeholder.enter.license.plate'
                        })}
                        icon="license_plate"
                        type="text"
                        onChange={e => this.onLicenseChange(e)}
                        value={licenseNumber}
                        validate={false}
                      />
                    </div>
                    <div className={`${carColorClassName} d-none d-sm-block`}>
                      {this.buildDropdown()}
                    </div>
                    <div className={`${carColorClassName} d-block d-sm-none`}>
                      <MobileDropdown
                        value={selectedColor}
                        options={getCarColors(intl).map(color => color.text)}
                        icon={'car_color'}
                        placeholder={intl.formatMessage({
                          id: 'c8.self.check.in.mileage.page.placeholder.color'
                        })}
                        onChange={e => this.onMobileDropdownToggle(e)}
                        className={`${ns}__mobile-dropdown`}
                      />
                    </div>
                  </div>
                </Col>
              </Row>
            </Container>
          </React.Fragment>
        )}
      />
    );
  }
}

export default MileagePage;



// WEBPACK FOOTER //
// ./src/components/pages/SelfCheckIn/MileagePage/MileagePage.js